
export interface SpeechInputConfig {
  language: string;
  onInterim?: (text: string) => void;
  onFinal: (text: string) => void;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}

export interface SpeechInputProvider {
  readonly supportsInterim: boolean;
  isAvailable(): Promise<boolean>;
  ensurePermissions(): Promise<void>;
  startListening(config: SpeechInputConfig): void;
  stopListening(): void;
  dispose(): void;
}

export class WebSpeechInputProvider implements SpeechInputProvider {
  readonly supportsInterim = true;
  private recognition: any = null;

  async isAvailable(): Promise<boolean> {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    return !!SR;
  }

  async ensurePermissions(): Promise<void> {
    try {
      if (navigator.permissions?.query) {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (result.state === 'denied') {
          throw new Error('Microphone permission denied');
        }
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Microphone permission denied') throw err;
    }
  }

  startListening(config: SpeechInputConfig): void {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) throw new Error('SpeechRecognition not available');

    this.stopListening();
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = config.language;

    recognition.onstart = () => config.onStart?.();

    recognition.onresult = (event: any) => {
      let finalText = '';
      let interimText = '';
      // Start from event.resultIndex — with continuous=true, event.results
      // accumulates all results. Starting from 0 re-processes old results.
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interimText += result[0].transcript;
        }
      }
      if (interimText) config.onInterim?.(interimText);
      if (finalText) config.onFinal(finalText);
    };

    recognition.onerror = (e: any) => {
      if (e.error !== 'no-speech' && e.error !== 'aborted') {
        config.onError?.(new Error(`Speech recognition error: ${e.error}`));
      }
    };

    recognition.onend = () => config.onEnd?.();

    this.recognition = recognition;
    try {
      recognition.start();
    } catch (_) { /* ignore duplicate start */ }
  }

  stopListening(): void {
    try {
      this.recognition?.stop();
    } catch (_) { /* ignore */ }
    this.recognition = null;
  }

  dispose(): void {
    this.stopListening();
  }
}

export function createSpeechInputProvider(): SpeechInputProvider {
  return new WebSpeechInputProvider();
}
